/** Default period for new organizations (Permanent). */
export const DEFAULT_ORGANIZATION_PERIOD_ID = '833c5feb-30b5-4190-a5c9-ba9644432cde';

export function isPermanentPeriod(period) {
  if (!period) return false;
  if (period.is_permanent === true || period.is_permanent === 't' || period.is_permanent === 1) {
    return true;
  }
  if (period.is_permanent === false || period.is_permanent === 'f' || period.is_permanent === 0) {
    return false;
  }
  // Legacy fallback when flag missing from a partial payload
  if (String(period.id) === DEFAULT_ORGANIZATION_PERIOD_ID) return true;
  const title = String(period.title || '').toLowerCase();
  const code = String(period.code || '').toLowerCase();
  return title.includes('permanent') || code.includes('permanent');
}

/** Prefer configured Permanent period id, then title/code match, then first list item. */
export function resolveDefaultOrganizationPeriodId(periods = []) {
  const list = Array.isArray(periods) ? periods : [];
  const byId = list.find((p) => String(p.id) === DEFAULT_ORGANIZATION_PERIOD_ID);
  if (byId) return String(byId.id);
  const byName = list.find((p) => isPermanentPeriod(p));
  if (byName) return String(byName.id);
  if (list[0]?.id) return String(list[0].id);
  return DEFAULT_ORGANIZATION_PERIOD_ID;
}

/** Period id for the org’s active groups_periods row (browse context). */
export function resolveOrgContextPeriodId(school, schoolGpId, periods = []) {
  const gp =
    school?.periods?.find((p) => String(p.id) === String(schoolGpId)) || school?.periods?.[0];
  if (gp?.period_id) return String(gp.period_id);
  return resolveDefaultOrganizationPeriodId(periods);
}

/** Auto groups_periods.description for new organizations on Permanent. */
export function formatOrganizationGroupPeriodDescription(organizationName) {
  const name = String(organizationName || '').trim() || 'Organization';
  return `${name} - Permanent`;
}

/** Operational periods only (excludes Permanent). */
export function filterSchoolYearPeriods(periods = []) {
  return (periods || []).filter((p) => !isPermanentPeriod(p));
}

export function resolveDefaultSchoolYearPeriodId(periods = []) {
  const list = filterSchoolYearPeriods(periods);
  return list[0]?.id ? String(list[0].id) : '';
}

/** Parent team's groups_periods.id for a school-year periods.id (definition row). */
export function resolveGroupPeriodGpId(parentGroup, periodDefinitionId) {
  if (!parentGroup?.periods?.length || !periodDefinitionId) return null;
  const gp = parentGroup.periods.find(
    (p) => String(p.period_id) === String(periodDefinitionId)
  );
  return gp?.id != null ? String(gp.id) : null;
}

function gpEnrollmentPeriodLabel(gp) {
  if (!gp) return '';
  return gp.period_title || gp.title || gp.period_code || gp.code || '';
}

/** Parent team + period for a nested team (from hierarchy metadata on related groups). */
export function resolveParentEnrollmentContext(anchorGroup, allGroups = []) {
  if (!anchorGroup) {
    return { parentTeamName: null, parentPeriodLabel: null };
  }
  const parentGpId = anchorGroup.parent_groups_periods_id;
  let parentTeamName = anchorGroup.parent_group_name || null;
  let parentPeriodLabel = null;

  if (parentGpId && Array.isArray(allGroups)) {
    for (const g of allGroups) {
      const gp = (g.periods || []).find((p) => String(p.id) === String(parentGpId));
      if (gp) {
        parentPeriodLabel = gpEnrollmentPeriodLabel(gp) || null;
        if (!parentTeamName) parentTeamName = g.name || null;
        break;
      }
    }
  }

  return { parentTeamName, parentPeriodLabel };
}

/**
 * @param {object} [context]
 * @param {'team_period'|'nested_team'} [context.mode]
 * @param {string} [context.teamName] — anchor team when adding another period on same group
 * @param {string} [context.parentTeamName]
 * @param {string} [context.parentPeriodLabel] — e.g. 2024-2025 on Student
 */
/** Short label for groups_periods.title (group + period). */
export function suggestGroupPeriodTitle(groupName, period) {
  const g = String(groupName || 'Group').trim() || 'Group';
  const periodLabel = period?.title || period?.code || 'Period';
  return `${g} – ${periodLabel}`;
}

/** Default "About this group" text from the group name (and org / parent context). */
export function suggestGroupDescription(groupName, orgName, context = null) {
  const g = String(groupName || 'Group').trim() || 'Group';
  if (context?.mode === 'nested_team') {
    const parentTeam = String(context.parentTeamName || '').trim();
    if (parentTeam) return `${g} of ${parentTeam}`;
  }
  const org = String(orgName || '').trim();
  if (org) return `${g} of ${org}`;
  return g;
}

/** Unique trimmed group names from a multiline textarea (one name per line). */
export function parseBatchGroupNames(text) {
  const seen = new Set();
  const names = [];
  for (const line of String(text || '').split(/\r?\n/)) {
    const name = line.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names;
}

export function buildGroupPeriodComboSuggests(groupName, period, orgName, context = null) {
  const name = String(groupName || 'Group').trim() || 'Group';
  return {
    groupPeriodTitle: suggestGroupPeriodTitle(name, period),
    groupPeriodDescription: suggestGroupPeriodDescription(name, period, orgName, context)
  };
}

export function suggestGroupPeriodDescription(groupName, period, orgName, context = null) {
  const g = String(groupName || 'Group').trim() || 'Group';
  const newPeriodLabel = period?.title || period?.code || 'this period';
  const org = String(orgName || '').trim();

  if (context?.mode === 'team_period') {
    const team = String(context.teamName || g).trim() || g;
    const parentTeam = String(context.parentTeamName || '').trim();
    const parentPeriod = String(context.parentPeriodLabel || '').trim();
    if (parentTeam && parentPeriod) {
      return `${team} ${newPeriodLabel} of ${parentTeam} in ${parentPeriod}`;
    }
    if (parentTeam) return `${team} ${newPeriodLabel} of ${parentTeam}`;
    return `${team} in ${newPeriodLabel}`;
  }

  if (context?.mode === 'nested_team') {
    const parentTeam = String(context.parentTeamName || '').trim();
    const parentPeriod = String(context.parentPeriodLabel || '').trim();
    if (parentTeam && parentPeriod) return `${g} of ${parentTeam} in ${parentPeriod}`;
    if (parentTeam) return `${g} of ${parentTeam}`;
  }

  if (org) return `${g} of ${org} in ${newPeriodLabel}`;
  return `${g} in ${newPeriodLabel}`;
}

export function getEmptyCreateEntityForm(
  entityKind,
  { periods = [], school = null, schoolGpId = null, requireExplicitPeriod = false } = {}
) {
  const base = {
    name: '',
    description: '',
    periodId: '',
    groupPeriodTitle: '',
    groupPeriodDescription: ''
  };
  if (entityKind === 'organization') {
    return { ...base, periodId: resolveDefaultOrganizationPeriodId(periods) };
  }
  if (entityKind === 'group') {
    return {
      ...base,
      periodId: requireExplicitPeriod ? '' : resolveDefaultSchoolYearPeriodId(periods)
    };
  }
  return base;
}

/** periods.id for a groups_periods row on a team. */
export function periodDefinitionIdFromTeamGp(gp) {
  if (!gp) return '';
  return gp.period_id != null ? String(gp.period_id) : '';
}

/** Minimal periods row built from a team's groups_periods enrollment. */
export function periodDefinitionFromTeamGp(gp) {
  const id = periodDefinitionIdFromTeamGp(gp);
  if (!id) return null;
  return {
    id,
    code: gp.period_code || gp.code || '',
    title: gp.period_title || gp.title || gp.period_code || '',
    start_date: gp.start_date || null,
    end_date: gp.end_date || null,
    is_permanent: gp.is_permanent === true || gp.is_permanent === 't' || gp.is_permanent === 1
  };
}

/** True when the group already has a Permanent period enrollment (sole-period rule). */
export function groupHasPermanentPeriod(group) {
  return (group?.periods || []).some((gp) => {
    const def = periodDefinitionFromTeamGp(gp);
    if (def) return isPermanentPeriod(def);
    return isPermanentPeriod({
      id: gp.period_id,
      title: gp.period_title || gp.title,
      code: gp.period_code || gp.code,
      is_permanent: gp.is_permanent
    });
  });
}

/** Period definition ids already on a team (Permanent included — can't add twice). */
export function periodDefinitionIdsOnTeam(anchorGroup) {
  const ids = new Set();
  for (const gp of anchorGroup?.periods || []) {
    const id = periodDefinitionIdFromTeamGp(gp);
    if (id) ids.add(id);
  }
  return ids;
}

function mergePeriodDefinitionList(periods = [], extraDefinitions = []) {
  const byId = new Map();
  for (const p of periods || []) {
    if (p?.id) byId.set(String(p.id), p);
  }
  for (const p of extraDefinitions || []) {
    if (!p?.id) continue;
    if (!byId.has(String(p.id))) byId.set(String(p.id), p);
  }
  return Array.from(byId.values());
}

/**
 * Period picklist for create-group under a focused team.
 * nested_team: include attach period (same period as parent) even if absent from org API list.
 * team_period: exclude periods the anchor team already has.
 * Permanent is exclusive on a group (sole period only).
 */
export function resolveCreateGroupPeriodOptions({
  periods = [],
  anchorGroup = null,
  addMode = 'team_period',
  parentAttachGpId = null
} = {}) {
  const base = periods || [];
  if (!anchorGroup) return base;

  const extras = [];
  if (parentAttachGpId) {
    const attachGp = (anchorGroup.periods || []).find(
      (gp) => String(gp.id) === String(parentAttachGpId)
    );
    const attachDef = periodDefinitionFromTeamGp(attachGp);
    if (attachDef) extras.push(attachDef);
  }

  let options = mergePeriodDefinitionList(base, extras);
  const existingGps = anchorGroup.periods || [];
  const hasExisting = existingGps.length > 0;
  const hasPermanent = existingGps.some((gp) => {
    const def = periodDefinitionFromTeamGp(gp);
    return def ? isPermanentPeriod(def) : isPermanentPeriod(gp);
  });

  if (addMode === 'team_period') {
    // Permanent is exclusive — cannot add anything alongside it
    if (hasPermanent) return [];
    const used = periodDefinitionIdsOnTeam(anchorGroup);
    options = options.filter((p) => !used.has(String(p.id)));
    // Terms already on the group — Permanent is not available
    if (hasExisting) {
      options = options.filter((p) => !isPermanentPeriod(p));
    }
  }

  return options;
}

/** Resolve nested team's school-year period from the parent attach groups_periods row. */
export function resolveNestedTeamPeriodFromAttach(anchorGroup, parentAttachGpId, periods = []) {
  if (!anchorGroup || !parentAttachGpId) return null;
  const attachGp = (anchorGroup.periods || []).find(
    (gp) => String(gp.id) === String(parentAttachGpId)
  );
  const periodDefId = periodDefinitionIdFromTeamGp(attachGp);
  if (!periodDefId) return null;
  const periodDef =
    periods.find((p) => String(p.id) === String(periodDefId)) ||
    periodDefinitionFromTeamGp(attachGp);
  if (!periodDef) return null;
  return { periodDefId, periodDef, attachGp };
}
